import { useCallback, useEffect, useState } from "react";
import { Flex, Table } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import styled from "styled-components";
import ScrollBox from "../common/Table/ScrollBox";
import { getShiftEmployeesOnDate } from "../../api/shiftDefinitionApi";
import { formatDayKey, todayKey, toDayKey } from "../../utils/shiftPattern";

/**
 * "Bu vardiyada kimler çalışıyor" — belirli bir TARİHTE (plan 2.24).
 *
 * Tarih seçici zorunlu bir parça, süs değil: atamalar tarih aralıklı olduğu
 * için "bu vardiyanın personeli" sorusunun tarihten bağımsız bir cevabı yok.
 * Dönüşümlü vardiyada aynı vardiyada bu hafta başka, gelecek hafta başka
 * kişiler bulunur.
 *
 * Eskiden bu iş iki ayrı ekrandı (`ShiftEmployeesTable` + `AllShiftEmployees
 * Table`), ikisi de backend'de olmayan `/weekly-shift` uçlarına bakıyordu.
 */
const ShiftEmployeesTable = ({ shiftDefinitionId }: { shiftDefinitionId: string }) => {
  // Mantine 8'de DatePickerInput değeri "YYYY-MM-DD" string.
  const [date, setDate] = useState<string | null>(todayKey());
  const [rows, setRows] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    const dayKey = toDayKey(date);
    if (!dayKey || !shiftDefinitionId) return;
    setIsLoading(true);
    try {
      const data: any = await getShiftEmployeesOnDate(shiftDefinitionId, dayKey);
      setRows(data?.data ?? []);
    } catch {
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [date, shiftDefinitionId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <Flex align="center" gap={12} mb={16}>
        <DatePickerInput
          label="Tarih"
          w={200}
          valueFormat="DD.MM.YYYY"
          value={date}
          onChange={setDate}
        />
      </Flex>

      {isLoading ? null : rows.length === 0 ? (
        <EmptyState>
          {formatDayKey(toDayKey(date))} tarihinde bu vardiyada çalışan yok.
        </EmptyState>
      ) : (
        <TableWrapper>
          <Table verticalSpacing="sm" style={{ borderCollapse: "separate" }}>
            <Table.Thead style={{ background: "#EEF2FF" }}>
              <Table.Tr>
                <StyledTh style={{ width: "40%" }}>Personel</StyledTh>
                <StyledTh style={{ width: "30%" }}>Atama Başlangıcı</StyledTh>
                <StyledTh style={{ width: "30%" }}>Atama Bitişi</StyledTh>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row._id}>
                  <Table.Td>{row.employee?.name ?? "—"}</Table.Td>
                  <Table.Td style={{ color: "#4b5563" }}>
                    {formatDayKey(row.startDate)}
                  </Table.Td>
                  <Table.Td style={{ color: "#4b5563" }}>
                    {row.endDate ? formatDayKey(row.endDate) : "Süresiz"}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </TableWrapper>
      )}
    </>
  );
};

export default ShiftEmployeesTable;

const TableWrapper = styled(ScrollBox)`
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  overflow-y: auto;
`;

const EmptyState = styled.div`
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  padding: 24px;
  color: #6b7280;
`;

const StyledTh = styled(Table.Td)`
  color: #4c4c4c;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
`;
